import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  CatalogAdminEditionClient,
  CatalogVersionDto,
  ConfirmUploadResult,
  CorrelationEdgeView,
  CreateSkillInputDto,
  EditCorrelationEdgeInputDto,
  EditProfileInputDto,
  ProfileCampaignUsageEntry,
  ProfileView,
  SkillView,
  UpdateSkillInputDto,
  UploadDryRunPreview,
} from '@/api/catalog-client';
import { useCatalogAdminEditionClient } from '@/api/catalog-client-context';

// =============================================================================
// use-catalog-edition.ts — Hooks TanStack Query d'ÉCRITURE admin du BC catalog
// (A-09 profil + matrice + A-25 compétences + A-10 upload ; S9-D4).
// =============================================================================
// Lectures de preview (profiles, edges, skills) en useQuery ; écritures (upsert,
// create/update/familles/archive, upload/confirm/cancel) en useMutation. Le client
// effectif vient du CatalogAdminEditionClientProvider (doublable, stub-first).
//
// ZÉRO règle métier ici : l'autorité (RBAC, inv.1 unicité, inv.2 édition réservée DRAFT,
// poids ∈ [0,1], inv.7 promotion bloquée si anomalie bloquante, dry-run SANS création de
// version) est l'API. Les hooks relaient l'intention ; l'erreur API remonte telle quelle.
// Après mutation, les queries impactées sont invalidées pour refléter l'état serveur.

const CATALOG_EDITION_KEY = ['catalog', 'admin', 'edition'] as const;

/** Clé de query des Profiles d'une version (A-09). */
export function catalogProfilesKey(catalogVersionId: string): readonly unknown[] {
  return [...CATALOG_EDITION_KEY, 'profiles', catalogVersionId];
}

/** Clé de query des arêtes de corrélation d'une version (matrice). */
export function catalogEdgesKey(catalogVersionId: string): readonly unknown[] {
  return [...CATALOG_EDITION_KEY, 'edges', catalogVersionId];
}

/** Clé de query de l'usage campagne d'un profil (avertissement d'archivage §2.4). */
export function profileCampaignUsageKey(
  catalogVersionId: string,
  profileCode: string,
): readonly unknown[] {
  return [...CATALOG_EDITION_KEY, 'campaign-usage', catalogVersionId, profileCode];
}

/** Clé de query de la liste des compétences (A-25). */
export const catalogSkillsKey = [...CATALOG_EDITION_KEY, 'skills'] as const;

/** Clé de query de l'usage campagne d'une compétence (avertissement d'archivage R15-CAT-03). */
export function skillCampaignUsageKey(skillCode: string): readonly unknown[] {
  return [...CATALOG_EDITION_KEY, 'skill-campaign-usage', skillCode];
}

// --- A-09 profil ---

/** GET preview des Profiles d'une version DRAFT (A-09). */
export function useCatalogProfiles(
  catalogVersionId: string,
  enabled = true,
): UseQueryResult<readonly ProfileView[], Error> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<readonly ProfileView[], Error>({
    queryKey: catalogProfilesKey(catalogVersionId),
    queryFn: () => client.listProfiles(catalogVersionId),
    // S14-bis C1-bis : permet de NE PAS requêter tant que la version catalogue (figée, détail campagne)
    // n'est pas connue. Défaut `true` ⇒ rétrocompatible avec l'écran d'édition catalogue.
    enabled,
  });
}

/** Variables de l'upsert d'un Profile (PUT). */
export interface UpsertProfileVars {
  readonly catalogVersionId: string;
  readonly input: EditProfileInputDto;
}

/** PUT upsert d'un Profile (création si profileCode absent). Invalide la preview. */
export function useUpsertProfile(): UseMutationResult<
  ProfileView,
  Error,
  UpsertProfileVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<ProfileView, Error, UpsertProfileVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'upsert-profile'],
    mutationFn: ({ catalogVersionId, input }) =>
      client.upsertProfile(catalogVersionId, input),
    onSuccess: (_data, { catalogVersionId }) => {
      void qc.invalidateQueries({ queryKey: catalogProfilesKey(catalogVersionId) });
    },
  });
}

/** Variables de l'archivage d'un Profile (POST .../archive). */
export interface ArchiveProfileVars {
  readonly catalogVersionId: string;
  readonly profileCode: string;
}

/**
 * POST archive un Profile (A-CATALOG §2.4, action DISTINCTE semi-destructive : state=Archived +
 * cascade des arêtes). Au succès, on invalide la preview des Profiles ET des arêtes de la version
 * (la cascade a pu basculer des arêtes en Archived — la matrice active doit se rafraîchir). 409
 * (`CATALOG_VERSION_WRONG_STATE` / `PROFILE_ALREADY_ARCHIVED`) : l'erreur remonte telle quelle
 * (avec son `code`) — l'autorité est l'API.
 */
export function useArchiveProfile(): UseMutationResult<
  ProfileView,
  Error,
  ArchiveProfileVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<ProfileView, Error, ArchiveProfileVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'archive-profile'],
    mutationFn: ({ catalogVersionId, profileCode }) =>
      client.archiveProfile(catalogVersionId, profileCode),
    onSuccess: (_data, { catalogVersionId }) => {
      void qc.invalidateQueries({ queryKey: catalogProfilesKey(catalogVersionId) });
      void qc.invalidateQueries({ queryKey: catalogEdgesKey(catalogVersionId) });
    },
  });
}

/**
 * POST restaure un Profile archivé (DBU §2.4 l.960, DEC-8 CODE-ONLY). Au succès, invalide la preview
 * des Profiles de la version (l'arête reste Archived — la matrice n'a PAS à changer, mais on la garde
 * cohérente en invalidant les deux clés par prudence). 409 (`CATALOG_VERSION_WRONG_STATE` /
 * `PROFILE_NOT_ARCHIVED`) : l'erreur remonte telle quelle (autorité = API).
 */
export function useRestoreProfile(): UseMutationResult<
  ProfileView,
  Error,
  ArchiveProfileVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<ProfileView, Error, ArchiveProfileVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'restore-profile'],
    mutationFn: ({ catalogVersionId, profileCode }) =>
      client.restoreProfile(catalogVersionId, profileCode),
    onSuccess: (_data, { catalogVersionId }) => {
      void qc.invalidateQueries({ queryKey: catalogProfilesKey(catalogVersionId) });
    },
  });
}

/**
 * POST clone un Profile (code neuf, contenu copié, 0 arête, DBU §2.4 l.950). Au succès, invalide la
 * preview des Profiles (le clone apparaît). 409 (version ≠ DRAFT) : l'erreur remonte telle quelle.
 */
export function useCloneProfile(): UseMutationResult<
  ProfileView,
  Error,
  ArchiveProfileVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<ProfileView, Error, ArchiveProfileVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'clone-profile'],
    mutationFn: ({ catalogVersionId, profileCode }) =>
      client.cloneProfile(catalogVersionId, profileCode),
    onSuccess: (_data, { catalogVersionId }) => {
      void qc.invalidateQueries({ queryKey: catalogProfilesKey(catalogVersionId) });
    },
  });
}

/** Variables de la FUSION de deux Profiles (POST .../merge, R15-CAT-01). */
export interface MergeProfilesVars {
  readonly catalogVersionId: string;
  readonly sourceProfileCode: string;
  readonly targetProfileCode: string;
  /** Confirmation textuelle : DOIT égaler `sourceProfileCode` (garde-fou, autorité API). */
  readonly confirmationCode: string;
}

/**
 * POST fusionne deux Profiles (R15-CAT-01, DBU §2.4 l.866) : transfert des corrélations source→cible +
 * archivage de la source. Au succès, on invalide la preview des Profiles ET des arêtes de la version (la
 * source passe Archived + des arêtes ont été transférées/archivées — la matrice active doit se rafraîchir).
 * 409/400 (`CATALOG_VERSION_WRONG_STATE` / `PROFILE_MERGE_*`) : l'erreur remonte telle quelle (autorité = API).
 */
export function useMergeProfiles(): UseMutationResult<
  ProfileView,
  Error,
  MergeProfilesVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<ProfileView, Error, MergeProfilesVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'merge-profiles'],
    mutationFn: ({ catalogVersionId, sourceProfileCode, targetProfileCode, confirmationCode }) =>
      client.mergeProfiles(
        catalogVersionId,
        sourceProfileCode,
        targetProfileCode,
        confirmationCode,
      ),
    onSuccess: (_data, { catalogVersionId }) => {
      void qc.invalidateQueries({ queryKey: catalogProfilesKey(catalogVersionId) });
      void qc.invalidateQueries({ queryKey: catalogEdgesKey(catalogVersionId) });
    },
  });
}

/**
 * MUTATION à la demande : suggère un code de profil AFFICHABLE lors de la création assistée
 * (DBU §2.4 l.952). Déclenchée par un bouton (« Suggérer un code » / « Régénérer ») ; ce n'est
 * PAS une query auto-fetchée (aucune suggestion tant que l'admin ne la demande pas). L'allocation
 * RÉELLE reste au PUT (aucune réservation d'état côté serveur) : la suggestion est indicative.
 */
export function useSuggestProfileCode(): UseMutationResult<
  { readonly profileCode: string },
  Error,
  string
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useMutation<{ readonly profileCode: string }, Error, string>({
    mutationKey: [...CATALOG_EDITION_KEY, 'suggest-profile-code'],
    mutationFn: (catalogVersionId: string) =>
      client.suggestProfileCode(catalogVersionId),
  });
}

/** GET fiche d'un Profile (visualiser, DBU §2.4 l.950). `enabled` gouverne le déclenchement. */
export function useProfile(
  catalogVersionId: string,
  profileCode: string | null,
): UseQueryResult<ProfileView, Error> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<ProfileView, Error>({
    queryKey: [...catalogProfilesKey(catalogVersionId), 'detail', profileCode],
    queryFn: () => client.getProfile(catalogVersionId, profileCode ?? ''),
    enabled: profileCode !== null,
  });
}

/**
 * GET usage campagne EN COURS d'un profil (avertissement d'archivage §2.4, A0R-10). Actif seulement quand
 * un profil est ciblé (`profileCode !== null`) : la modale d'archivage pilote le déclenchement. LECTURE
 * SEULE, INFORMATIONNEL (l'avertissement n'implique jamais un blocage).
 */
export function useProfileCampaignUsage(
  catalogVersionId: string,
  profileCode: string | null,
): UseQueryResult<readonly ProfileCampaignUsageEntry[], Error> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<readonly ProfileCampaignUsageEntry[], Error>({
    queryKey: profileCampaignUsageKey(catalogVersionId, profileCode ?? ''),
    queryFn: () => client.listProfileCampaignUsage(catalogVersionId, profileCode ?? ''),
    enabled: profileCode !== null,
  });
}

/**
 * GET usage campagne EN COURS d'une COMPÉTENCE où elle est PROPOSABLE (avertissement d'archivage R15-CAT-03,
 * DEC-27). Actif seulement quand une compétence est ciblée (`skillCode !== null`) : la modale d'archivage
 * pilote le déclenchement. LECTURE SEULE, INFORMATIONNEL (l'avertissement n'implique jamais un blocage).
 */
export function useSkillCampaignUsage(
  skillCode: string | null,
): UseQueryResult<readonly ProfileCampaignUsageEntry[], Error> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<readonly ProfileCampaignUsageEntry[], Error>({
    queryKey: skillCampaignUsageKey(skillCode ?? ''),
    queryFn: () => client.listSkillCampaignUsage(skillCode ?? ''),
    enabled: skillCode !== null,
  });
}

// --- Matrice de corrélation ---

/** GET preview des arêtes de corrélation d'une version DRAFT (matrice). */
export function useCatalogEdges(
  catalogVersionId: string,
): UseQueryResult<readonly CorrelationEdgeView[], Error> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<readonly CorrelationEdgeView[], Error>({
    queryKey: catalogEdgesKey(catalogVersionId),
    queryFn: () => client.listEdges(catalogVersionId),
  });
}

/** Variables de l'upsert d'une arête (PUT). */
export interface UpsertEdgeVars {
  readonly catalogVersionId: string;
  readonly input: EditCorrelationEdgeInputDto;
}

/** PUT upsert d'une arête de corrélation (poids ∈ [0,1], validé serveur). */
export function useUpsertEdge(): UseMutationResult<
  CorrelationEdgeView,
  Error,
  UpsertEdgeVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<CorrelationEdgeView, Error, UpsertEdgeVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'upsert-edge'],
    mutationFn: ({ catalogVersionId, input }) =>
      client.upsertEdge(catalogVersionId, input),
    onSuccess: (_data, { catalogVersionId }) => {
      void qc.invalidateQueries({ queryKey: catalogEdgesKey(catalogVersionId) });
    },
  });
}

// --- A-08 promotion DRAFT→PUBLISHED (DBU §2.4) ---

/**
 * POST promotion DRAFT→PUBLISHED d'une version (DBU §2.4). L'API archive ATOMIQUEMENT la PUBLISHED
 * précédente (« une seule PUBLISHED ») + émet DE.CatalogVersionPublished. Au succès, le browser des
 * versions (A-08, clé `['catalog','admin','versions']`) est périmé : on l'invalide pour refléter les
 * nouveaux états. 409 (état ≠ DRAFT) : l'erreur remonte telle quelle (autorité = API).
 */
export function usePublishCatalogVersion(): UseMutationResult<
  CatalogVersionDto,
  Error,
  string
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<CatalogVersionDto, Error, string>({
    mutationKey: [...CATALOG_EDITION_KEY, 'publish-version'],
    mutationFn: (catalogVersionId: string) => client.publishVersion(catalogVersionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog', 'admin', 'versions'] });
    },
  });
}

// --- A-25 compétences ---

/** GET liste des compétences (avec familles associées). */
export function useCatalogSkills(): UseQueryResult<
  readonly SkillView[],
  Error
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<readonly SkillView[], Error>({
    queryKey: catalogSkillsKey,
    queryFn: () => client.listSkills(),
  });
}

/** Clé de cache du référentiel des familles associables (REM-A #1). */
export const associableFamiliesKey = [
  ...CATALOG_EDITION_KEY,
  'associable-families',
] as const;

/**
 * REM-A #3 (DBU §2.10 l.2274) — EXPORT CSV de la MATRICE d'associations Compétences×Familles. Mutation
 * (effet : produit un contenu CSV à télécharger, pas une lecture cachée). Renvoie le CONTENU CSV (durci API) ;
 * l'appelant déclenche l'enregistrement navigateur.
 */
export function useExportSkillsMatrixCsv(): UseMutationResult<string, Error, void> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useMutation<string, Error, void>({
    mutationKey: [...CATALOG_EDITION_KEY, 'export-skills-matrix'],
    mutationFn: () => client.exportSkillsMatrixCsv(),
  });
}

/**
 * REM-A #1 (DBU §2.10) — GET référentiel des familles de profils ASSOCIABLES (DISTINCT `Profile.famille`).
 * Alimente le SÉLECTEUR contraint d'association Compétence↔Familles (intégrité référentielle). Lecture pure.
 */
export function useAssociableFamilies(): UseQueryResult<
  readonly string[],
  Error
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useQuery<readonly string[], Error>({
    queryKey: associableFamiliesKey,
    queryFn: () => client.listAssociableFamilies(),
  });
}

/** POST création d'une compétence (inv.1 unicité, autorité serveur). */
export function useCreateSkill(): UseMutationResult<
  SkillView,
  Error,
  CreateSkillInputDto
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<SkillView, Error, CreateSkillInputDto>({
    mutationKey: [...CATALOG_EDITION_KEY, 'create-skill'],
    mutationFn: (input) => client.createSkill(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogSkillsKey });
    },
  });
}

/** Variables de la MAJ partielle d'une compétence (PATCH). */
export interface UpdateSkillVars {
  readonly skillCode: string;
  readonly input: UpdateSkillInputDto;
}

/** PATCH MAJ partielle d'une compétence (libellé/description/catégorie). */
export function useUpdateSkill(): UseMutationResult<
  SkillView,
  Error,
  UpdateSkillVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<SkillView, Error, UpdateSkillVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'update-skill'],
    mutationFn: ({ skillCode, input }) => client.updateSkill(skillCode, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogSkillsKey });
    },
  });
}

/** Variables de la réconciliation des familles (PUT). */
export interface SetSkillFamiliesVars {
  readonly skillCode: string;
  readonly familles: readonly string[];
}

/** PUT réconcilie les familles associées (associate/dissociate). */
export function useSetSkillFamilies(): UseMutationResult<
  SkillView,
  Error,
  SetSkillFamiliesVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<SkillView, Error, SetSkillFamiliesVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'set-skill-familles'],
    mutationFn: ({ skillCode, familles }) =>
      client.setSkillFamilies(skillCode, familles),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogSkillsKey });
    },
  });
}

/** POST archive une compétence (action DISTINCTE, no-DELETE : state=Archived). */
export function useArchiveSkill(): UseMutationResult<SkillView, Error, string> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<SkillView, Error, string>({
    mutationKey: [...CATALOG_EDITION_KEY, 'archive-skill'],
    mutationFn: (skillCode: string) => client.archiveSkill(skillCode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogSkillsKey });
    },
  });
}

/**
 * POST restaure une compétence archivée (Archivé → Actif, DBU §2.10 l.2256 ; #16). Primitive DÉDIÉE :
 * corrige le bug #16 (« Restaurer » passait par `useUpdateSkill({state:'Active'})`, un NO-OP côté serveur).
 * L'API émet DE.SkillRestored + rejoue l'invalidation de la liste. 409 `SKILL_NOT_ARCHIVED` remonte tel quel.
 */
export function useRestoreSkill(): UseMutationResult<SkillView, Error, string> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<SkillView, Error, string>({
    mutationKey: [...CATALOG_EDITION_KEY, 'restore-skill'],
    mutationFn: (skillCode: string) => client.restoreSkill(skillCode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: catalogSkillsKey });
    },
  });
}

// --- A-10 upload (dry-run) ---

/** POST upload + DRY-RUN (anomalies, SANS création de version, G0-S9-4). */
export function useUploadDryRun(): UseMutationResult<
  UploadDryRunPreview,
  Error,
  File
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useMutation<UploadDryRunPreview, Error, File>({
    mutationKey: [...CATALOG_EDITION_KEY, 'upload-dry-run'],
    mutationFn: (file: File) => client.uploadDryRun(file),
  });
}

/** Variables de la confirmation d'un upload (POST .../confirm). */
export interface ConfirmUploadVars {
  readonly attemptId: string;
  readonly versionNumber: string;
}

/** POST confirme un upload : promotion ATOMIQUE en DRAFT (G0-S9-5). */
export function useConfirmUpload(): UseMutationResult<
  ConfirmUploadResult,
  Error,
  ConfirmUploadVars
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  const qc = useQueryClient();
  return useMutation<ConfirmUploadResult, Error, ConfirmUploadVars>({
    mutationKey: [...CATALOG_EDITION_KEY, 'confirm-upload'],
    mutationFn: ({ attemptId, versionNumber }) =>
      client.confirmUpload(attemptId, versionNumber),
    onSuccess: () => {
      // Une nouvelle DRAFT est apparue : le browser des versions (A-08) est périmé.
      void qc.invalidateQueries({ queryKey: ['catalog', 'admin', 'versions'] });
    },
  });
}

/** POST annule un upload (→ Cancelled). */
export function useCancelUpload(): UseMutationResult<
  { readonly attemptId: string; readonly outcome: 'Cancelled' },
  Error,
  string
> {
  const client: CatalogAdminEditionClient = useCatalogAdminEditionClient();
  return useMutation<
    { readonly attemptId: string; readonly outcome: 'Cancelled' },
    Error,
    string
  >({
    mutationKey: [...CATALOG_EDITION_KEY, 'cancel-upload'],
    mutationFn: (attemptId: string) => client.cancelUpload(attemptId),
  });
}
